import { additionalTripCompanions } from "./gateway-expansion";

export type CoreCompanionAirportCode =
	| "FUK"
	| "KIX"
	| "NRT"
	| "HND"
	| "CTS"
	| "OKA"
	| "NGO"
	| "KMJ";
export type CompanionAirportCode =
	| CoreCompanionAirportCode
	| "SHI"
	| "SDJ"
	| "HSG"
	| "HKD"
	| "KKJ"
	| "OIT"
	| "KMI"
	| "KMQ"
	| "OKJ"
	| "OBO"
	| "HIJ"
	| "TKS"
	| "KIJ"
	| "MYJ"
	| "ISG"
	| "YGJ"
	| "IBR"
	| "NGS"
	| "AOJ"
	| "FSZ"
	| "TAK"
	| "KOJ";

export type SuggestedStop = {
	id: string;
	type: "spot" | "food" | "transport";
	title: string;
	area: string;
	when: string;
	duration: string;
	mood: string;
	estimateJpy: number | null;
	mapQuery: string;
};

export type TripCompanion = {
	bestFor: string;
	paceNote: string;
	neighborhoods: { name: string; character: string; stayReason: string }[];
	suggestedDays: { title: string; note: string; stopIds: string[] }[];
	stops: SuggestedStop[];
};

const coreTripCompanions: Record<CoreCompanionAirportCode, TripCompanion> = {
	FUK: {
		bestFor: "2박 3일 미식·산책",
		paceNote: "공항과 도심이 가까워 첫날과 마지막 날도 반나절을 쓰기 좋아요.",
		neighborhoods: [
			{
				name: "하카타",
				character: "교통과 식사가 빠른 관문",
				stayReason: "첫 일본·근교 이동이 많을 때",
			},
			{
				name: "텐진",
				character: "쇼핑과 밤 골목의 중심",
				stayReason: "도심을 늦게까지 걷고 싶을 때",
			},
			{
				name: "다이묘·이마이즈미",
				character: "작은 가게와 카페가 이어지는 골목",
				stayReason: "속도를 늦춘 재방문 여행",
			},
		],
		suggestedDays: [
			{
				title: "첫날 · 하카타에서 나카스",
				note: "짐을 풀고 강변의 밤까지 짧게 이어 가요.",
				stopIds: ["fuk-hakata", "fuk-canal", "fuk-yatai"],
			},
			{
				title: "둘째 날 · 공원과 바다",
				note: "오호리의 아침과 모모치의 해질녘을 묶어요.",
				stopIds: ["fuk-ohori", "fuk-momochi"],
			},
			{
				title: "셋째 날 · 다자이후",
				note: "귀국편 시간에 따라 오전만 다녀와요.",
				stopIds: ["fuk-dazaifu"],
			},
		],
		stops: [
			{
				id: "fuk-hakata",
				type: "spot",
				title: "하카타역과 옥상 정원",
				area: "하카타",
				when: "도착 뒤",
				duration: "1–2시간",
				mood: "첫 환승과 식사를 한 번에 해결해요.",
				estimateJpy: 0,
				mapQuery: "JR Hakata City",
			},
			{
				id: "fuk-canal",
				type: "spot",
				title: "캐널시티와 구시다 신사",
				area: "하카타",
				when: "오후",
				duration: "2시간",
				mood: "쇼핑몰에서 오래 머물기보다 주변 골목까지 걸어요.",
				estimateJpy: 0,
				mapQuery: "Canal City Hakata",
			},
			{
				id: "fuk-yatai",
				type: "food",
				title: "나카스·텐진 야타이",
				area: "나카스",
				when: "저녁",
				duration: "1–2시간",
				mood: "한 곳을 길게 점유하기보다 짧게 먹고 다음 골목으로 가요.",
				estimateJpy: 2500,
				mapQuery: "Nakasu Yatai",
			},
			{
				id: "fuk-ohori",
				type: "spot",
				title: "오호리공원과 후쿠오카성터",
				area: "주오구",
				when: "아침",
				duration: "2–3시간",
				mood: "도심 속 물가를 천천히 한 바퀴 걷는 시간이에요.",
				estimateJpy: 0,
				mapQuery: "Ohori Park",
			},
			{
				id: "fuk-momochi",
				type: "spot",
				title: "모모치해변과 후쿠오카타워",
				area: "모모치",
				when: "해질녘",
				duration: "2–3시간",
				mood: "바다와 도시 불빛을 함께 봐요.",
				estimateJpy: 1000,
				mapQuery: "Momochi Seaside Park",
			},
			{
				id: "fuk-dazaifu",
				type: "spot",
				title: "다자이후 참배길",
				area: "다자이후",
				when: "오전",
				duration: "반나절",
				mood: "매화떡과 작은 가게를 따라 천천히 올라가요.",
				estimateJpy: 1500,
				mapQuery: "Dazaifu Tenmangu",
			},
		],
	},
	KIX: {
		bestFor: "오사카 3일·교토 연계",
		paceNote:
			"간사이공항에서 숙소 도시가 갈리므로 첫날 동선을 욕심내지 않는 편이 좋아요.",
		neighborhoods: [
			{
				name: "난바",
				character: "먹거리와 쇼핑이 밀집한 남쪽 중심",
				stayReason: "첫 방문과 밤 일정",
			},
			{
				name: "우메다",
				character: "광역철도와 현대적인 도시 풍경",
				stayReason: "교토·고베를 함께 갈 때",
			},
			{
				name: "나카자키초",
				character: "낮은 집과 작은 카페 골목",
				stayReason: "번화가에서 한 걸음 벗어나고 싶을 때",
			},
		],
		suggestedDays: [
			{
				title: "첫날 · 난바의 입구",
				note: "도톤보리 한복판보다 호젠지 골목부터 걸어요.",
				stopIds: ["kix-namba", "kix-hozenji", "kix-okonomiyaki"],
			},
			{
				title: "둘째 날 · 성과 오래된 상점가",
				note: "오사카성과 텐진바시를 넓게 잇습니다.",
				stopIds: ["kix-castle", "kix-tenjinbashisuji"],
			},
			{
				title: "셋째 날 · 교토 또는 고베",
				note: "한 도시만 골라 왕복 시간을 줄여요.",
				stopIds: ["kix-kyoto"],
			},
		],
		stops: [
			{
				id: "kix-namba",
				type: "transport",
				title: "난바 숙소에 짐 맡기기",
				area: "난바",
				when: "도착 뒤",
				duration: "30분",
				mood: "공항 급행에서 내린 뒤 이동을 가볍게 시작해요.",
				estimateJpy: null,
				mapQuery: "Namba Station",
			},
			{
				id: "kix-hozenji",
				type: "spot",
				title: "호젠지 요코초",
				area: "난바",
				when: "해질녘",
				duration: "1시간",
				mood: "번화가 옆 짧고 젖은 돌길의 분위기를 봐요.",
				estimateJpy: 0,
				mapQuery: "Hozenji Yokocho",
			},
			{
				id: "kix-okonomiyaki",
				type: "food",
				title: "오코노미야키 한 판",
				area: "난바",
				when: "저녁",
				duration: "1시간",
				mood: "철판 앞에서 뜨겁게 먹는 오사카의 한 끼예요.",
				estimateJpy: 1800,
				mapQuery: "Okonomiyaki Namba",
			},
			{
				id: "kix-castle",
				type: "spot",
				title: "오사카성 공원",
				area: "주오구",
				when: "아침",
				duration: "2–3시간",
				mood: "성 내부와 공원 산책 중 무엇을 볼지 먼저 골라요.",
				estimateJpy: 1200,
				mapQuery: "Osaka Castle",
			},
			{
				id: "kix-tenjinbashisuji",
				type: "spot",
				title: "텐진바시스지 상점가",
				area: "기타구",
				when: "오후",
				duration: "2시간",
				mood: "생활 상점과 간식을 긴 아케이드에서 만나요.",
				estimateJpy: 1500,
				mapQuery: "Tenjinbashisuji Shopping Street",
			},
			{
				id: "kix-kyoto",
				type: "spot",
				title: "기온과 시라카와",
				area: "교토",
				when: "하루",
				duration: "반나절 이상",
				mood: "이동 시간을 포함해 교토 한 구역에 집중해요.",
				estimateJpy: 3000,
				mapQuery: "Gion Shirakawa",
			},
		],
	},
	NRT: {
		bestFor: "도쿄 4일 이상·나리타 산책",
		paceNote:
			"도심까지 길기 때문에 도착일에는 숙소 권역 하나만 잡는 편이 안전해요.",
		neighborhoods: [
			{
				name: "우에노",
				character: "공항철도와 박물관·시장",
				stayReason: "나리타 접근과 동쪽 도쿄",
			},
			{
				name: "아사쿠사",
				character: "이른 아침이 좋은 오래된 상점가",
				stayReason: "도쿄의 낮은 골목",
			},
			{
				name: "기요스미시라카와",
				character: "정원과 창고형 카페",
				stayReason: "재방문·느린 산책",
			},
		],
		suggestedDays: [
			{
				title: "첫날 · 우에노에 안착",
				note: "공항 이동 뒤 시장과 골목만 짧게 봐요.",
				stopIds: ["nrt-ueno", "nrt-ameyoko"],
			},
			{
				title: "둘째 날 · 아사쿠사에서 강 건너",
				note: "이른 시간 센소지와 스미다 강을 이어요.",
				stopIds: ["nrt-asakusa", "nrt-sumida"],
			},
			{
				title: "마지막 날 · 나리타 참배길",
				note: "늦은 귀국편일 때만 공항 가까이에서 마무리해요.",
				stopIds: ["nrt-naritasan"],
			},
		],
		stops: [
			{
				id: "nrt-ueno",
				type: "transport",
				title: "우에노 숙소에 짐 맡기기",
				area: "우에노",
				when: "도착 뒤",
				duration: "30분",
				mood: "긴 공항 이동 뒤 동선을 줄여요.",
				estimateJpy: null,
				mapQuery: "Keisei Ueno Station",
			},
			{
				id: "nrt-ameyoko",
				type: "food",
				title: "아메요코 저녁 골목",
				area: "우에노",
				when: "저녁",
				duration: "1–2시간",
				mood: "시장 간식과 작은 식당을 짧게 비교해요.",
				estimateJpy: 2500,
				mapQuery: "Ameyoko Shopping Street",
			},
			{
				id: "nrt-asakusa",
				type: "spot",
				title: "센소지와 뒷골목",
				area: "아사쿠사",
				when: "이른 아침",
				duration: "2시간",
				mood: "인파 전의 절과 상점가를 걸어요.",
				estimateJpy: 0,
				mapQuery: "Sensoji",
			},
			{
				id: "nrt-sumida",
				type: "spot",
				title: "스미다 강변",
				area: "아사쿠사",
				when: "오후",
				duration: "1–2시간",
				mood: "도쿄 스카이라인을 강 건너에서 봐요.",
				estimateJpy: 0,
				mapQuery: "Sumida Park",
			},
			{
				id: "nrt-naritasan",
				type: "spot",
				title: "나리타산 신쇼지 참배길",
				area: "나리타",
				when: "귀국 전",
				duration: "3시간",
				mood: "장어집과 센베이 가게가 이어지는 공항 근교 골목이에요.",
				estimateJpy: 2500,
				mapQuery: "Naritasan Shinshoji",
			},
		],
	},
	HND: {
		bestFor: "도쿄 짧은 일정·늦은 도착",
		paceNote:
			"도심과 가까워도 퇴근 시간 혼잡을 감안해 첫 저녁은 숙소 권역에서 보내요.",
		neighborhoods: [
			{
				name: "시나가와",
				character: "공항과 신칸센이 만나는 관문",
				stayReason: "짧은 일정·지방 연계",
			},
			{
				name: "긴자·신바시",
				character: "낮과 밤의 표정이 다른 중심가",
				stayReason: "도심 이동을 줄일 때",
			},
			{
				name: "요코하마",
				character: "항구 산책과 야경",
				stayReason: "도쿄 남쪽을 중심으로 볼 때",
			},
		],
		suggestedDays: [
			{
				title: "첫날 · 시나가와와 신바시",
				note: "늦은 도착에도 무리 없는 짧은 저녁이에요.",
				stopIds: ["hnd-shinagawa", "hnd-shimbashi"],
			},
			{
				title: "둘째 날 · 시장에서 정원으로",
				note: "아침 식사와 하마리큐를 걸어서 이어요.",
				stopIds: ["hnd-tsukiji", "hnd-hamarikyu"],
			},
			{
				title: "셋째 날 · 요코하마 항구",
				note: "공항 남쪽으로 방향을 정해 왕복을 줄여요.",
				stopIds: ["hnd-yokohama"],
			},
		],
		stops: [
			{
				id: "hnd-shinagawa",
				type: "transport",
				title: "시나가와에 짐 풀기",
				area: "시나가와",
				when: "도착 뒤",
				duration: "30분",
				mood: "게이큐에서 내려 다음 동선을 가볍게 해요.",
				estimateJpy: null,
				mapQuery: "Shinagawa Station",
			},
			{
				id: "hnd-shimbashi",
				type: "food",
				title: "신바시 골목 저녁",
				area: "신바시",
				when: "저녁",
				duration: "2시간",
				mood: "작은 식당이 이어지는 직장인 거리의 저녁이에요.",
				estimateJpy: 3000,
				mapQuery: "Shimbashi Yokocho",
			},
			{
				id: "hnd-tsukiji",
				type: "food",
				title: "츠키지 장외시장 아침",
				area: "츠키지",
				when: "아침",
				duration: "1–2시간",
				mood: "한 번에 많이 먹기보다 작은 메뉴를 나눠 봐요.",
				estimateJpy: 2500,
				mapQuery: "Tsukiji Outer Market",
			},
			{
				id: "hnd-hamarikyu",
				type: "spot",
				title: "하마리큐 정원",
				area: "시오도메",
				when: "오전",
				duration: "2시간",
				mood: "고층 건물 사이 오래된 정원의 여백을 봐요.",
				estimateJpy: 300,
				mapQuery: "Hamarikyu Gardens",
			},
			{
				id: "hnd-yokohama",
				type: "spot",
				title: "요코하마 미나토미라이",
				area: "요코하마",
				when: "오후·저녁",
				duration: "반나절",
				mood: "붉은 벽돌 창고에서 항구 야경까지 걸어요.",
				estimateJpy: 2000,
				mapQuery: "Yokohama Red Brick Warehouse",
			},
		],
	},
	CTS: {
		bestFor: "삿포로 3일·설경과 온천",
		paceNote: "폭설과 넓은 이동거리를 고려해 하루 핵심 지역을 하나만 정해요.",
		neighborhoods: [
			{
				name: "삿포로역",
				character: "JR과 지하 통로의 중심",
				stayReason: "눈 오는 날과 근교 이동",
			},
			{
				name: "오도리",
				character: "공원과 상점가가 맞닿은 중심",
				stayReason: "도심 산책",
			},
			{
				name: "마루야마",
				character: "신궁·공원·작은 식당",
				stayReason: "조용한 재방문 여행",
			},
		],
		suggestedDays: [
			{
				title: "첫날 · 지하로 이어지는 삿포로",
				note: "눈이 와도 움직이기 쉬운 역과 오도리부터 봐요.",
				stopIds: ["cts-sapporo", "cts-odori", "cts-ramen"],
			},
			{
				title: "둘째 날 · 시장과 마루야마",
				note: "아침 시장 뒤 조용한 서쪽 동네로 가요.",
				stopIds: ["cts-nijo", "cts-maruyama"],
			},
			{
				title: "셋째 날 · 오타루",
				note: "기상과 JR 운행을 확인한 뒤 하루를 비워 둬요.",
				stopIds: ["cts-otaru"],
			},
		],
		stops: [
			{
				id: "cts-sapporo",
				type: "transport",
				title: "삿포로역 지하 동선 익히기",
				area: "삿포로역",
				when: "도착 뒤",
				duration: "1시간",
				mood: "눈길 대신 지하 통로의 출구 번호를 익혀요.",
				estimateJpy: 0,
				mapQuery: "Sapporo Station",
			},
			{
				id: "cts-odori",
				type: "spot",
				title: "오도리공원과 TV타워",
				area: "오도리",
				when: "해질녘",
				duration: "1–2시간",
				mood: "계절 행사와 날씨에 따라 머무는 시간을 조절해요.",
				estimateJpy: 1000,
				mapQuery: "Odori Park",
			},
			{
				id: "cts-ramen",
				type: "food",
				title: "미소라멘 한 그릇",
				area: "스스키노",
				when: "저녁",
				duration: "1시간",
				mood: "추운 날 진한 국물로 마무리해요.",
				estimateJpy: 1300,
				mapQuery: "Sapporo Miso Ramen",
			},
			{
				id: "cts-nijo",
				type: "food",
				title: "니조시장 아침",
				area: "니조",
				when: "아침",
				duration: "1시간",
				mood: "가격과 양을 보고 해산물덮밥을 골라요.",
				estimateJpy: 2500,
				mapQuery: "Nijo Market",
			},
			{
				id: "cts-maruyama",
				type: "spot",
				title: "홋카이도신궁과 마루야마",
				area: "마루야마",
				when: "오후",
				duration: "2–3시간",
				mood: "눈 덮인 숲길과 동네 카페를 천천히 걸어요.",
				estimateJpy: 800,
				mapQuery: "Hokkaido Jingu",
			},
			{
				id: "cts-otaru",
				type: "spot",
				title: "오타루 운하와 사카이마치",
				area: "오타루",
				when: "하루",
				duration: "반나절 이상",
				mood: "운하보다 오래된 상점과 유리 공방 골목을 함께 봐요.",
				estimateJpy: 3500,
				mapQuery: "Otaru Canal",
			},
		],
	},
	OKA: {
		bestFor: "나하 3일·바다와 드라이브",
		paceNote:
			"북부 이동은 도로 정체가 커서 나하 도시일과 렌터카 일을 분리해요.",
		neighborhoods: [
			{
				name: "국제거리·마키시",
				character: "시장과 식당이 이어지는 중심",
				stayReason: "첫 방문·차 없는 일정",
			},
			{
				name: "슈리",
				character: "언덕과 오래된 돌길",
				stayReason: "역사와 느린 산책",
			},
			{
				name: "차탄",
				character: "해변과 해질녘",
				stayReason: "중부·렌터카 일정",
			},
		],
		suggestedDays: [
			{
				title: "첫날 · 나하 시장 골목",
				note: "모노레일로 들어와 시장과 저녁을 가볍게 이어요.",
				stopIds: ["oka-makishi", "oka-soba"],
			},
			{
				title: "둘째 날 · 슈리의 돌길",
				note: "햇빛이 약한 오전에 언덕을 먼저 걸어요.",
				stopIds: ["oka-shuri", "oka-ishidatami"],
			},
			{
				title: "셋째 날 · 바다 한 방향",
				note: "차탄 또는 북부 중 하나만 골라요.",
				stopIds: ["oka-chatan"],
			},
		],
		stops: [
			{
				id: "oka-makishi",
				type: "spot",
				title: "마키시 공설시장과 골목",
				area: "나하",
				when: "도착 뒤",
				duration: "2시간",
				mood: "시장 2층 식당과 주변 상점가를 함께 봐요.",
				estimateJpy: 1000,
				mapQuery: "Makishi Public Market",
			},
			{
				id: "oka-soba",
				type: "food",
				title: "오키나와 소바",
				area: "나하",
				when: "점심·저녁",
				duration: "1시간",
				mood: "가게마다 다른 면과 국물을 비교해요.",
				estimateJpy: 1000,
				mapQuery: "Okinawa Soba Naha",
			},
			{
				id: "oka-shuri",
				type: "spot",
				title: "슈리성공원",
				area: "슈리",
				when: "오전",
				duration: "2–3시간",
				mood: "복원 중인 공간의 현재 안내를 확인하며 걸어요.",
				estimateJpy: 400,
				mapQuery: "Shurijo Castle Park",
			},
			{
				id: "oka-ishidatami",
				type: "spot",
				title: "슈리 긴조초 돌다다미길",
				area: "슈리",
				when: "오전",
				duration: "1시간",
				mood: "미끄러운 돌길이라 비 오는 날 신발을 살펴요.",
				estimateJpy: 0,
				mapQuery: "Shuri Kinjocho Stone Path",
			},
			{
				id: "oka-chatan",
				type: "spot",
				title: "차탄 해변과 선셋",
				area: "차탄",
				when: "오후·저녁",
				duration: "반나절",
				mood: "바다를 보고 저녁까지 머무는 중부 일정이에요.",
				estimateJpy: 2000,
				mapQuery: "Chatan Sunset Beach",
			},
		],
	},
	NGO: {
		bestFor: "나고야 3일·중부 연계",
		paceNote:
			"관광지가 흩어져 있어 지하철 권역과 장거리 근교를 같은 날 섞지 않아요.",
		neighborhoods: [
			{
				name: "나고야역",
				character: "공항과 근교 교통의 중심",
				stayReason: "다카야마·기후 연계",
			},
			{
				name: "사카에",
				character: "백화점과 야경의 중심",
				stayReason: "도심 일정",
			},
			{
				name: "오스",
				character: "절과 상점가·서브컬처",
				stayReason: "골목과 먹거리",
			},
		],
		suggestedDays: [
			{
				title: "첫날 · 역에서 사카에",
				note: "나고야메시 한 가지와 야경만 골라요.",
				stopIds: ["ngo-nagoya", "ngo-sakae", "ngo-hitsumabushi"],
			},
			{
				title: "둘째 날 · 성과 오스",
				note: "북쪽 성에서 남쪽 상점가로 내려와요.",
				stopIds: ["ngo-castle", "ngo-osu"],
			},
			{
				title: "셋째 날 · 이누야마",
				note: "메이테츠로 오래된 성하마을을 다녀와요.",
				stopIds: ["ngo-inuyama"],
			},
		],
		stops: [
			{
				id: "ngo-nagoya",
				type: "transport",
				title: "나고야역 동선 익히기",
				area: "나고야역",
				when: "도착 뒤",
				duration: "1시간",
				mood: "메이테츠·JR·지하철 출구를 먼저 구분해요.",
				estimateJpy: 0,
				mapQuery: "Nagoya Station",
			},
			{
				id: "ngo-sakae",
				type: "spot",
				title: "사카에와 미라이타워",
				area: "사카에",
				when: "해질녘",
				duration: "2시간",
				mood: "넓은 도로와 입체 공원의 야경을 봐요.",
				estimateJpy: 1300,
				mapQuery: "Chubu Electric Power MIRAI TOWER",
			},
			{
				id: "ngo-hitsumabushi",
				type: "food",
				title: "히츠마부시",
				area: "사카에",
				when: "저녁",
				duration: "1시간",
				mood: "세 가지 방식으로 나눠 먹는 나고야의 장어 한 상이에요.",
				estimateJpy: 4500,
				mapQuery: "Hitsumabushi Nagoya",
			},
			{
				id: "ngo-castle",
				type: "spot",
				title: "나고야성 일대",
				area: "나고야성",
				when: "오전",
				duration: "2시간",
				mood: "공개 구역과 공사 상태를 공식 안내에서 확인해요.",
				estimateJpy: 500,
				mapQuery: "Nagoya Castle",
			},
			{
				id: "ngo-osu",
				type: "spot",
				title: "오스 상점가",
				area: "오스",
				when: "오후",
				duration: "2–3시간",
				mood: "절, 빈티지, 전자상가와 간식이 섞인 골목이에요.",
				estimateJpy: 1800,
				mapQuery: "Osu Shopping Street",
			},
			{
				id: "ngo-inuyama",
				type: "spot",
				title: "이누야마 성하마을",
				area: "이누야마",
				when: "하루",
				duration: "반나절",
				mood: "작은 성과 오래된 상점가를 메이테츠로 다녀와요.",
				estimateJpy: 3000,
				mapQuery: "Inuyama Castle",
			},
		],
	},
	KMJ: {
		bestFor: "구마모토 3일·아소와 온천",
		paceNote:
			"버스 예약과 도로 상태가 여행을 좌우하므로 시내일과 아소일을 분리해요.",
		neighborhoods: [
			{
				name: "사쿠라마치·성",
				character: "버스와 성곽의 중심",
				stayReason: "차 없는 첫 방문",
			},
			{
				name: "가미토리·시모토리",
				character: "비를 피할 수 있는 아케이드 골목",
				stayReason: "먹거리와 밤 산책",
			},
			{
				name: "아소",
				character: "화산 지형과 넓은 초원",
				stayReason: "자연·렌터카 일정",
			},
		],
		suggestedDays: [
			{
				title: "첫날 · 성 아래 골목",
				note: "버스에서 내려 성과 아케이드를 짧게 이어요.",
				stopIds: ["kmj-sakuramachi", "kmj-castle", "kmj-arcade"],
			},
			{
				title: "둘째 날 · 아소",
				note: "화산 경보와 교통을 확인하고 하루를 비워 둬요.",
				stopIds: ["kmj-aso"],
			},
			{
				title: "셋째 날 · 정원과 한 끼",
				note: "귀국 전 시내 동쪽을 천천히 봐요.",
				stopIds: ["kmj-suizenji", "kmj-ramen"],
			},
		],
		stops: [
			{
				id: "kmj-sakuramachi",
				type: "transport",
				title: "사쿠라마치 버스터미널",
				area: "사쿠라마치",
				when: "도착 뒤",
				duration: "30분",
				mood: "아소·온천 버스 승강장과 다음 날 표를 먼저 확인해요.",
				estimateJpy: 0,
				mapQuery: "Sakura Machi Kumamoto",
			},
			{
				id: "kmj-castle",
				type: "spot",
				title: "구마모토성",
				area: "주오구",
				when: "오후",
				duration: "2–3시간",
				mood: "복구 과정과 성 아래 풍경을 함께 봐요.",
				estimateJpy: 800,
				mapQuery: "Kumamoto Castle",
			},
			{
				id: "kmj-arcade",
				type: "food",
				title: "가미토리·시모토리 저녁",
				area: "주오구",
				when: "저녁",
				duration: "2시간",
				mood: "비 오는 날에도 걷기 좋은 아케이드에서 한 끼를 골라요.",
				estimateJpy: 2500,
				mapQuery: "Shimotori Shopping Arcade",
			},
			{
				id: "kmj-aso",
				type: "spot",
				title: "아소산과 구사센리",
				area: "아소",
				when: "하루",
				duration: "하루",
				mood: "화산 경보·날씨·버스 운행이 모두 맞을 때 가요.",
				estimateJpy: 4500,
				mapQuery: "Kusasenri Aso",
			},
			{
				id: "kmj-suizenji",
				type: "spot",
				title: "스이젠지 조주엔",
				area: "스이젠지",
				when: "오전",
				duration: "1–2시간",
				mood: "작은 산수 정원의 길을 천천히 돌아요.",
				estimateJpy: 400,
				mapQuery: "Suizenji Jojuen Garden",
			},
			{
				id: "kmj-ramen",
				type: "food",
				title: "구마모토 라멘",
				area: "구마모토역·도심",
				when: "점심",
				duration: "1시간",
				mood: "마늘향이 진한 국물로 여행을 마무리해요.",
				estimateJpy: 1100,
				mapQuery: "Kumamoto Ramen",
			},
		],
	},
};

export const tripCompanions: Record<CompanionAirportCode, TripCompanion> = {
	...coreTripCompanions,
	...additionalTripCompanions,
};

export const officialTravelTools = [
	{
		id: "visit-japan-web",
		title: "Visit Japan Web",
		description: "입국심사·세관 신고를 준비하는 일본 디지털청 공식 서비스",
		url: "https://services.digital.go.jp/en/visit-japan-web/",
		kind: "entry" as const,
	},
	{
		id: "jnto-safety",
		title: "JNTO 안전 정보",
		description: "재난·분실·의료 상황과 24시간 Visitor Hotline 안내",
		url: "https://www.japan.travel/en/plan/emergencies/",
		kind: "safety" as const,
	},
	{
		id: "jma",
		title: "일본 기상청 방재 정보",
		description: "기상 경보·지진·태풍의 공식 현재 정보",
		url: "https://www.jma.go.jp/bosai/#lang=en",
		kind: "safety" as const,
	},
	{
		id: "navitime",
		title: "NAVITIME for Japan Travel",
		description: "일본 내 대중교통 상세 경로와 운행 확인",
		url: "https://japantravel.navitime.com/en/",
		kind: "route" as const,
	},
	{
		id: "maps-offline",
		title: "Google Maps offline 안내",
		description: "출발 전 지도 영역 저장 방법과 offline 제한 확인",
		url: "https://support.google.com/maps/answer/6291838?hl=ko",
		kind: "route" as const,
	},
];

export const essentialPhrases = [
	{
		ko: "실례합니다. 이곳에 가고 싶어요.",
		ja: "すみません、ここに行きたいです。",
		reading: "스미마센, 코코니 이키타이데스",
	},
	{
		ko: "이 열차는 ○○에 가나요?",
		ja: "この電車は○○に行きますか？",
		reading: "코노 덴샤와 ○○니 이키마스카",
	},
	{
		ko: "교통카드를 사용할 수 있나요?",
		ja: "交通系ICカードは使えますか？",
		reading: "코츠케이 아이시카도와 츠카에마스카",
	},
	{ ko: "도와주세요.", ja: "助けてください。", reading: "타스케테 쿠다사이" },
	{
		ko: "한국어 통역이 필요해요.",
		ja: "韓国語の通訳が必要です。",
		reading: "칸코쿠고노 츠야쿠가 히츠요데스",
	},
];

export const googleMapsSearchUrl = (query: string) =>
	`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
export const googleMapsDirectionsUrl = (query: string) =>
	`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;
export const googleMapsEmbedUrl = (query: string) =>
	`https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;

/** Google Maps URLs official waypoint limit: three intermediate stops on mobile. */
export function dayDirectionsSegments(
	items: { title: string; area: string; mapQuery: string | null }[],
) {
	const points = items.map((p) => p.mapQuery || `${p.title} ${p.area}`);
	const rows: { label: string; url: string }[] = [];
	for (let start = 0; start < points.length - 1; ) {
		let end = Math.min(points.length - 1, start + 4);
		let url = "";
		while (end > start) {
			const params = new URLSearchParams({
				api: "1",
				origin: points[start]!,
				destination: points[end]!,
			});
			if (end - start > 1)
				params.set("waypoints", points.slice(start + 1, end).join("|"));
			url = `https://www.google.com/maps/dir/?${params}`;
			if (url.length <= 2048) break;
			end--;
		}
		if (end === start) break;
		rows.push({
			label: `${rows.length + 1}구간(${start + 1}~${end + 1})`,
			url,
		});
		start = end;
	}
	return rows;
}
